/**
 * The smallest possible element helper, for the portal's own markup.
 *
 * **A near-copy of `src/client-admin/src/dom.ts`, and that is the decision rather than an
 * oversight.** plan/portal-design.md weighs sharing a component library across the two bundles
 * and says no, on four grounds — the deliberately asymmetric type boundary (admin DTOs are
 * excluded from the OpenAPI document and refused a place in `Tunet.Contracts`), the
 * no-sideways-references rule that `SurfaceBoundaryTests` enforces on the server and nothing
 * enforces in this layer, the small genuine overlap, and the asymmetric failure modes: drift in
 * a shared *stylesheet* is a visible cosmetic difference, drift in a shared *component package*
 * is a build failure in two bundles at once. Thirty lines of `createElement` is the cheapest
 * possible thing to duplicate and the most expensive possible thing to couple two bundles over.
 *
 * `text` is assigned through `textContent` and every attribute through `setAttribute`, so
 * nothing here can interpolate a server string into markup. That is not a stylistic preference:
 * room names, display names and org names are all user-supplied and land in this page verbatim.
 * The page this replaces built its member table with `innerHTML` and an `escapeHtml` helper —
 * correct, but one forgotten call away from not being.
 */

type Attrs = Record<string, string | number | boolean | null | undefined>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [name, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) {
      continue;
    }

    if (name === 'class') {
      node.className = String(value);
    } else if (name === 'text') {
      node.textContent = String(value);
    } else {
      node.setAttribute(name, String(value));
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }

    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

/** Replaces a container's contents in one go. */
export function replace(container: Element, ...children: Child[]): void {
  container.replaceChildren();

  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }

    container.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
}
